import { recordEvenementClick, recordEvent, recordFormationView } from '@mtc/shared';
import { gaEvent } from './analytics';

/**
 * Thin, fire-and-forget tracking wrappers for the in-admin engagement panel.
 *
 * Rules:
 *   - Never throw — analytics must never break the page. All calls swallow errors.
 *   - Formation views are de-duped per browser session (a refresh or revisit in
 *     the same tab session doesn't double-count). Intentional clicks are NOT
 *     de-duped (a click is a deliberate action worth counting each time).
 *
 * This is separate from GA4 (added later) — GA4 handles visitor counts,
 * sources and the funnel; this just powers the "most-viewed formations" +
 * key-click totals shown directly in the admin dashboard.
 */

function seenThisSession(key: string): boolean {
  try {
    if (sessionStorage.getItem(key)) return true;
    sessionStorage.setItem(key, '1');
    return false;
  } catch {
    // sessionStorage unavailable (private mode, etc.) → just count it.
    return false;
  }
}

/** Count a formation detail-page view, once per session per formation. */
export function trackFormationView(slug: string): void {
  if (!slug) return;
  if (seenThisSession(`mtc_view_${slug}`)) return;
  void recordFormationView(slug).catch(() => {});
  // GA4 mirror (no-op without consent). Lets you rank formations in GA too.
  gaEvent('formation_view', { formation_id: slug });
}

/** Recognised engagement events (keep names stable — they're counter keys). */
export type TrackedEvent =
  | 'sinscrire_click'
  | 'reserver_click'
  | 'whatsapp_click'
  | 'affiche_download'
  | 'gallery_open'
  | 'inscription_complete'
  | 'publications_open';

/** Count an intentional engagement click/action. */
export function trackEvent(name: TrackedEvent): void {
  void recordEvent(name).catch(() => {});
  // GA4 mirror (no-op without consent).
  gaEvent(name);
}

/** Count a click on « S'inscrire à l'événement » (event detail CTA), per event slug. */
export function trackEvenementInscriptionClick(slug: string): void {
  if (!slug) return;
  void recordEvenementClick('inscription', slug).catch(() => {});
  gaEvent('evenement_inscription_click', { evenement_id: slug });
}

/** Count a click on the Home featured-event (« vedette ») block, per event slug. */
export function trackEvenementVedetteClick(slug: string): void {
  if (!slug) return;
  void recordEvenementClick('vedette', slug).catch(() => {});
  gaEvent('evenement_vedette_click', { evenement_id: slug });
}

/**
 * Inscription funnel step — GA4 only (not Firestore; too granular for the
 * in-admin counters). Lets you build a step-by-step drop-off funnel in GA4.
 */
export function trackInscriptionStep(step: number, stepName: string): void {
  gaEvent('inscription_step', { step, step_name: stepName });
}
